import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { prepareTableData } from "../../../features/dataFormatFunctions/dataFormatter";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import LoaderWrapper from "../../../widgets/loaderWrapper/LoaderWrapper";
import { defineTableColumnsFromArray } from "../../../widgets/table/tableFeatures/tableFunctions";
import TableWidget from "../../../widgets/table/tableWidget/TableWidget";
import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import { useFetchRoadDocumentsQuery, useFetchObjectDocumentsQuery } from "../../../api/rtk/base/slices/documentsApiSlice";
import { buildFileDownloadBodyTemplate, buildFilePreviewBodyTemplate, fileIconBodyTemplate, filePreviewBodyTemplate, fileSizeBodyTemplate } from "../../../widgets/table/tableFeatures/tableTemplates/tableBodyTemplates/documents";
import { useDispatch } from "react-redux";
import TableButtonsRow from "../../../widgets/table/tableAddons/tableButtonsRow/TableButtonsRow";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import { setDownloadSelectedDocumentsButtonIsClicked } from "../../../stateManagement/reducers/TableButtonsRowSlice";
import { downloadDocumentFile, downloadSelectedDocumentFiles } from "./tableDocumentsFunctions";
import { setDocumentsDownloadingState, showToastMessage } from "../../../stateManagement/reducers";
import ToastMessageContainer from "../../../widgets/toastMessageContainer/ToastMessageContainer";
import { navigateTo } from "../../../widgets/navigationService/navigationServiceFunctions";

const fileIconSrc = require('../../../assets/images/fontAwesomeIcons/file-solid.svg').default

const columnsStructure = [
    {
        headerTemplate: <img src={fileIconSrc} height={23} width={17} />,
        field: 'FileExtension',
        align: 'center',
        valueType: "string",
        bodyTemplate: fileIconBodyTemplate
    },
    {
        headerName: "Имя документа",
        field: 'Name',
        align: 'left',
        valueType: "string",
    },
    {
        headerName: "Расширение",
        field: 'FileExtension',
        align: 'left',
        valueType: "string",
    },
    {
        headerName: "Тип документа",
        field: 'DocumentTypeName',
        align: 'left',
        valueType: "string"
    },
    {
        headerName: "Размер файла",
        field: 'DataSize',
        align: 'left',
        valueType: "string",
        bodyTemplate: fileSizeBodyTemplate
    },
    {
        headerName: "Просмотреть",
        field: 'DocPreview@xdata.proxy',
        align: 'center',
        valueType: "string",
        bodyTemplate: buildFilePreviewBodyTemplate({
            propForDocumentId: "DocumentID",
            propForFileExtension: "FileExtension",
            previewFunction(id) {
                navigateTo(`${id}`, { relative: true })
            },
        })
    },
    {
        headerName: "Скачать",
        field: 'Data@xdata.proxy',
        align: 'center',
        valueType: "string"
    },
    {
        selectionMode: "multiple",
        align: "center"
    }
]

export default function TableDocuments() {
    const dispatch = useDispatch()
    const { roadId, objectId, objectTypeName }: any = useParams();
    const [columns, setColumns] = useState<any>(null)
    const [tableValue, setTableValue] = useState<any>([])
    const { documentsDownloadingState, documentsTableName } = useAppSelector(state => state.documents) ?? {}
    const [selectedDocuments, setSelectedDocuments] = useState<any>([])
    const downloadSelectedDocumentsButtonIsClicked = useAppSelector(state => state.tableButtonsRow.downloadSelectedDocumentsButtonIsClicked)
    const addressTypes = getItemWebStorage('addressTypes')
    const urlType = addressTypes?.find((type: any) => type.name === objectTypeName) != null ? `address` : `objects`

    // запрос на получение документов дороги (отправляется при наличии id дороги)
    const {
        data: roadDocuments,
        isFetching: roadDocumentsAreFetching,
        isError: errorRoadDocuments
    } = useFetchRoadDocumentsQuery({ roadId }, { skip: !roadId })

    // запрос на получение документов объекта (отправляется при наличии id объекта)
    const {
        data: objectDocuments,
        isFetching: objectDocumentsAreFetching,
        isError: errorObjectDocuments
    } = useFetchObjectDocumentsQuery({ urlType, objectType: objectTypeName, objectId }, { skip: !objectId })

    useEffect(() => {
        const cols = defineTableColumnsFromArray(columnsStructure);

        const bodyTemplate = buildFileDownloadBodyTemplate({
            propForDocumentId: 'DocumentID',
            propForFileExtension: 'FileExtension',
            propForDocumentName: 'Name',
            propForLinkType: 'LinkType',
            downloadFunction: downloadDocumentFile,
            documentsDownloadingState,
        });

        const fileDownloadColumnIndex = cols.findIndex(
            (col: any) => col.field === 'Data@xdata.proxy'
        );

        cols[fileDownloadColumnIndex] = {
            ...cols[fileDownloadColumnIndex],
            bodyTemplate,
        };

        setColumns(cols);
    }, [documentsDownloadingState]);

    useEffect(() => {
        if (columns && (objectDocuments || roadDocuments)) {
            let data = roadDocuments?.value ?? objectDocuments?.value
            let value = prepareTableData(data, columns)
            setTableValue(value)
        }
    }, [columns, roadDocuments, objectDocuments])

    useEffect(() => {
        if (downloadSelectedDocumentsButtonIsClicked) {
            dispatch(setDownloadSelectedDocumentsButtonIsClicked(false))
            if (selectedDocuments.length > 0) {
                downloadSelectedDocumentFiles(selectedDocuments)
            } else {
                dispatch(showToastMessage({ messageText: "Выберите документы для скачивания", messageType: "info" }))
            }
        }
    }, [downloadSelectedDocumentsButtonIsClicked])

    useEffect(() => {
        return () => {
            dispatch(setDocumentsDownloadingState(undefined))
        }
    }, [])


    useEffect(() => {
        if (errorRoadDocuments || errorObjectDocuments) {
            dispatch(showToastMessage({ messageText: "Ошибка загрузки таблицы", messageType: "error" }))
        }
    }, [errorRoadDocuments, errorObjectDocuments])

    return (
        <>
            <ContentHeader title={documentsTableName ?? "Документы"} type="simple" />
            <div className="table-widget-with-toast with-content-header">
                <ToastMessageContainer />
                <LoaderWrapper isLoading={roadDocumentsAreFetching || objectDocumentsAreFetching} >
                    <TableWidget
                        tableValue={tableValue}
                        columns={columns}
                        canBeFiltered={false}
                        canBeSorted={false}
                        selection={selectedDocuments}
                        onSelectionChange={(event: any) => setSelectedDocuments(event.value)}
                        withButtonsRow
                    />
                    <TableButtonsRow type="documents" />
                </LoaderWrapper>
            </div >
        </>
    )
}

